import { Controller } from "@hotwired/stimulus"

// Provides instant visual feedback on navigation via skeleton screens.
//
// Two behaviours:
//   1. Instantly updates the bottom nav active tab on click.
//   2. Shows a skeleton after a 150ms threshold if the page hasn't rendered yet.
//      Cache hits (~0-50ms) cancel the timer before it fires — no skeleton shown.
//      Only slow or network-bound responses actually trigger the skeleton.
//
// Skeleton targeting:
//   - Pages with a <turbo-frame> wrapping list content: only the frame is
//     skeletoned. The header, search bar, and controls above it stay real.
//   - Pages without a list frame (Home, More, sub-pages): content below
//     the header is replaced with the matching full-page skeleton.
//
// Skeleton templates live in app/views/field/shared/_skeletons.html.erb
// as <template> elements — HTML stays in ERB, never in JS.
export default class extends Controller {
  connect() {
    this._onBeforeVisit  = this._handleBeforeVisit.bind(this)
    this._onBeforeRender = this._cancelSkeleton.bind(this)
    this._onBeforeCache  = this._restoreBeforeCache.bind(this)
    this._onLoad         = this._clearStash.bind(this)
    window.addEventListener("turbo:before-visit",  this._onBeforeVisit)
    window.addEventListener("turbo:before-render", this._onBeforeRender)
    window.addEventListener("turbo:before-cache",  this._onBeforeCache)
    window.addEventListener("turbo:load",          this._onLoad)
  }

  disconnect() {
    window.removeEventListener("turbo:before-visit",  this._onBeforeVisit)
    window.removeEventListener("turbo:before-render", this._onBeforeRender)
    window.removeEventListener("turbo:before-cache",  this._onBeforeCache)
    window.removeEventListener("turbo:load",          this._onLoad)
    this._cancelSkeleton()
  }

  _handleBeforeVisit(event) {
    const path = new URL(event.detail.url).pathname

    this._highlightNav(path)

    // Wait 150ms before showing skeleton — cache hits render faster than this
    // and should never show a loading state
    this._skeletonTimer = setTimeout(() => this._injectSkeleton(path), 150)
  }

  _cancelSkeleton() {
    if (this._skeletonTimer) {
      clearTimeout(this._skeletonTimer)
      this._skeletonTimer = null
    }
  }

  // Instantly update bottom nav active state without waiting for page render
  _highlightNav(path) {
    document.querySelectorAll("[data-nav-path]").forEach(link => {
      const navPath = link.dataset.navPath
      const isActive = navPath === "/field"
        ? (path === "/field" || path === "/field/")
        : path.startsWith(navPath)

      link.classList.toggle("text-sapphire", isActive)
      link.classList.toggle("text-slate-400", !isActive)
    })
  }

  // Turbo snapshots the page being LEFT on turbo:before-cache. If a skeleton is
  // still painted in when that happens, the frozen skeleton gets cached and a
  // later restoration visit (Back) has no way to replace it. Restore the real
  // content before Turbo takes the snapshot so cached snapshots are never
  // skeletoned.
  _restoreBeforeCache() {
    if (!this._skeletonInjected) return

    const mainContent = document.getElementById("main-content")
    if (mainContent && this._stashedContent != null) {
      const permanent = this._permanentElements(mainContent)
      mainContent.innerHTML = this._stashedContent
      // The stash is a string, so it holds copies of any permanent elements.
      // Put the live ones back in their place, or Turbo would carry the copies
      // to the next page instead (the sync queue bar, card #3863).
      permanent.forEach(live => document.getElementById(live.id)?.replaceWith(live))
    }
    this._clearStash()
  }

  // data-turbo-permanent elements keep their state across visits, so the
  // skeleton must never drop or copy them.
  _permanentElements(root) {
    return [...root.querySelectorAll("[id][data-turbo-permanent]")]
  }

  // Fresh page render — the DOM is the new page, there is nothing to restore.
  _clearStash() {
    this._skeletonInjected = false
    this._stashedContent   = null
  }

  _injectSkeleton(path) {
    const mainContent = document.getElementById("main-content")
    if (!mainContent) return

    // Already skeletoned — don't stash the skeleton over the real content.
    if (this._skeletonInjected) return

    // Stash the real content before we overwrite it, so turbo:before-cache can
    // restore it ahead of the snapshot.
    this._stashedContent   = mainContent.innerHTML
    this._skeletonInjected = true

    // If the page has a turbo-frame wrapping list content, only skeleton
    // inside that frame. The search bar, filters, and controls above it
    // are static/cached and should stay visible.
    const listFrame = mainContent.querySelector("turbo-frame")
    if (listFrame) {
      const cardsTemplate = document.getElementById("skeleton-cards")
      if (cardsTemplate) {
        listFrame.innerHTML = ""
        listFrame.appendChild(cardsTemplate.content.cloneNode(true))
      }
      return
    }

    // No list frame — replace content below the header with a full-page skeleton
    const template = document.getElementById(this._templateIdFor(path))
    if (!template) return

    const clone = template.content.cloneNode(true)
    clone.querySelector("header")?.remove()

    const existingHeader = mainContent.querySelector("header")
    const permanent      = this._permanentElements(mainContent)
    mainContent.innerHTML = ""
    if (existingHeader) mainContent.appendChild(existingHeader)
    permanent.forEach(el => mainContent.appendChild(el))
    mainContent.appendChild(clone)
  }

  _templateIdFor(path) {
    if (path === "/field" || path === "/field/") return "skeleton-home"

    if (
      path.startsWith("/field/more") ||
      path.startsWith("/field/settings") ||
      path.startsWith("/field/profile") ||
      path.startsWith("/field/sync") ||
      path.startsWith("/field/storage") ||
      path.startsWith("/field/changelog")
    ) return "skeleton-more"

    return "skeleton-default"
  }
}
